/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { aiModelsService } from '../../services/ai-models.service';
import { promptRunService } from '../../services/prompt-run.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { PromptRunResponse } from '../../types';
import EvalRunResult from './eval-run-result';
import AiFeedbackBar from '../ai-feedback/ai-feedback-bar';

interface PromptRunPanelProps {
  promptId: string;
  /** Run this version (the draft) instead of the active one. Attached evals test
   *  the active version, so they are not offered for it. */
  version?: number;
  /** The prompt's own code, for the feedback definition that resolves on it. */
  promptCode?: string | null;
  /** The live version, used when this run is not pinned to a draft. */
  activeVersion?: number | null;
}

const fmtCost = (cost?: number, currency?: string) => {
  if (cost === null || cost === undefined) return '-';
  const cur = currency ? `${currency} ` : '$';
  return `${cur}${cost.toFixed(cost < 1 ? 4 : 2)}`;
};

/** Parse a JSON object string; empty → undefined; invalid → throws. */
const parseVariables = (raw: string): Record<string, unknown> | undefined => {
  const t = raw.trim();
  if (!t) return undefined;
  const parsed = JSON.parse(t);
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Variables must be a JSON object');
  }
  return parsed as Record<string, unknown>;
};

/**
 * Test-run form + live results for a stored prompt. Meant to be rendered inside a
 * Dialog. Runs the prompt then polls each triggered eval run to completion.
 */
const PromptRunPanel = ({ promptId, version, promptCode, activeVersion }: PromptRunPanelProps) => {
  const [input, setInput] = useState('');
  const [variables, setVariables] = useState('');
  const [modelId, setModelId] = useState<string | null>(null);
  const [maxTokens, setMaxTokens] = useState<number | null>(null);
  const [runEvals, setRunEvals] = useState(true);
  const [varsError, setVarsError] = useState('');

  const { data: aiModelsResp } = useQuery({
    // runnable_only: an embedding model cannot be sent a prompt, so offering
    // one here can only produce a run that fails at the provider.
    queryKey: ['ai-models', 'runnable'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100, runnable_only: true }),
    staleTime: 5 * 60 * 1000,
  });
  // Send the unique _id, not modelId: several rows can share a modelId (three
  // here are called "gpt-oss-120b"), and the resolver rejects an ambiguous name
  // outright — so picking one of those from this list could only ever fail.
  // Same as eval-run-trigger-panel.
  const modelOptions = (aiModelsResp?.data || []).map((m) => ({
    value: m._id,
    label: m.displayName ? `${m.displayName} (${m.modelId})` : m.modelId,
  }));

  const mutation = useMutation<PromptRunResponse, any, void>({
    mutationFn: async () => {
      let vars: Record<string, unknown> | undefined;
      try {
        vars = parseVariables(variables);
        setVarsError('');
      } catch (e: any) {
        setVarsError(e?.message || 'Invalid JSON');
        throw e;
      }
      return promptRunService.execute(promptId, {
        input: input.trim() || undefined,
        variables: vars,
        modelId: modelId || undefined,
        maxTokens: maxTokens ?? undefined,
        runEvals: version ? false : runEvals,
        version,
      });
    },
  });

  const result = mutation.data;

  return (
    <div className="flex flex-col gap-3">
      {/* Form */}
      <div className="grid grid-cols-1 gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Input</label>
          <InputTextarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={3}
            placeholder="Optional input text"
            className="w-full"
            autoResize
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Variables (JSON object)
          </label>
          <InputTextarea
            value={variables}
            onChange={(e) => setVariables(e.target.value)}
            rows={3}
            placeholder='{ "name": "value" }'
            className="w-full font-mono text-sm"
            autoResize
          />
          {varsError && <span className="text-xs text-red-500">{varsError}</span>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Model override
            </label>
            <Dropdown
              value={modelId}
              options={modelOptions}
              onChange={(e: any) => setModelId(e.value || null)}
              placeholder="Use prompt default"
              filter
              showClear
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Max Tokens
            </label>
            <InputNumber
              value={maxTokens}
              onValueChange={(e) => setMaxTokens(e.value ?? null)}
              useGrouping={false}
              placeholder="Model default"
              className="w-full"
            />
          </div>
        </div>

        {version ? (
          <p className="text-xs text-ink-muted">
            Running <strong>draft v{version}</strong>. The live prompt is not changed, and attached evals are not run — they test the active version.
          </p>
        ) : (
        <div className="flex items-center gap-2">
          <Checkbox
            inputId="runEvals"
            checked={runEvals}
            onChange={(e) => setRunEvals(!!e.checked)}
          />
          <label htmlFor="runEvals" className="text-sm text-gray-700 dark:text-gray-300">
            Run evals after execution
          </label>
        </div>
        )}

        <div className="flex justify-end">
          <Button
            label={mutation.isPending ? 'Running…' : 'Run'}
            icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            size="small"
          />
        </div>
      </div>

      {mutation.isError && !varsError && (
        <Message
          severity="error"
          text={extractErrorMessage(mutation.error, 'Failed to run prompt')}
          className="w-full"
        />
      )}

      {mutation.isPending && (
        <div className="flex items-center gap-2 p-3 text-sm text-ink-muted">
          <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="6" />
          Executing…
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="flex flex-col gap-3">
          <div className="ds-card p-3">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h3 className="text-sm font-semibold text-ink">Output</h3>
              {result.servedModel && (
                <Tag value={result.servedModel} severity="info" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <pre className="text-sm text-ink whitespace-pre-wrap font-sans break-words">
                {result.output || '-'}
              </pre>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mt-3">
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Input Tokens</label>
                <p className="text-sm text-ink">{result.usage?.inputTokens ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Output Tokens</label>
                <p className="text-sm text-ink">{result.usage?.outputTokens ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Total Tokens</label>
                <p className="text-sm text-ink">{result.usage?.totalTokens ?? '-'}</p>
              </div>
              <div>
                <label className="text-xs text-ink-muted block mb-0.5">Cost</label>
                <p className="text-sm text-ink">{fmtCost(result.cost, result.currency)}</p>
              </div>
            </div>

            {/* On the answer, not on the dialog: this is the artifact a person can
                judge, and the execution's own id is what makes the judgement
                reproducible after the dialog is closed. An ad-hoc run persists no
                execution row, so there is nothing honest to anchor on and the bar
                stays away rather than filing an opinion about nothing. */}
            {result.executionId && (
              <AiFeedbackBar
                className="mt-3 border-t border-line pt-3"
                componentType="PROMPT"
                componentCode={promptCode || undefined}
                componentId={result.executionId}
                componentVersion={`v${version ?? activeVersion ?? 1}`}
                /* Keys are the ones the PROMPT component type declares in
                   `feedbackComponentContextKey` — userInput / modelResponse /
                   modelName / promptVersion. A key the definition does not declare
                   is dropped at write, so a reviewer only ever sees what the
                   definition said it would keep. */
                runtimeContext={{
                  userInput: input.trim() || variables.trim() || null,
                  modelResponse: result.output || null,
                  modelName: result.servedModel || result.model || null,
                  promptVersion: version ?? activeVersion ?? null,
                  latencyMs: result.executionTime != null
                    ? Math.round(result.executionTime * 1000) : null,
                  tokensIn: result.usage?.inputTokens ?? null,
                  tokensOut: result.usage?.outputTokens ?? null,
                  cost: result.cost ?? null,
                }}
                meta={result.servedModel || undefined}
                testId="PromptRun.Feedback"
              />
            )}
          </div>

          {/* Eval runs triggered by this execution */}
          {result.evalRuns && result.evalRuns.length > 0 && (
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-ink">Eval Runs</h3>
              {result.evalRuns.map((er, i) => {
                if (er.evalRunId) {
                  return (
                    <EvalRunResult
                      key={er.evalRunId}
                      runRef={er.evalRunId}
                      title={er.suiteCode || er.suiteRef || 'Eval Run'}
                    />
                  );
                }
                return (
                  <div
                    key={er.suiteRef || er.suiteCode || i}
                    className="border border-line rounded p-3 bg-gray-50 dark:bg-gray-700/40 flex items-center gap-2 flex-wrap"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {er.suiteCode || er.suiteRef || 'Eval'}
                    </span>
                    {er.status && (
                      <Tag value={er.status} severity="warning" className="text-xs px-1.5 py-0.5" />
                    )}
                    {er.reason && (
                      <span className="text-xs text-ink-muted">{er.reason}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptRunPanel;
